import { type Common } from './common.js';
import { type PaginatedQueryOptions } from './models/pagination/PaginatedQueryOptions.type.js';
import { type PaginationResponse } from './models/pagination/PaginationResponse.type.js';
import {
  type CsvBulkUpdateValues,
  type EhrProviderInformation,
  type FindAllOptions,
  type ImportValidation,
  type Provider,
  type ProviderCsv,
  type ProviderWithEnabledPractices,
  type UpsertValues,
} from './models/provider.js';
import { type User, type UserBasic } from './models/user.js';

export class API {
  common: Common;
  baseEndpoint = '/provider';

  constructor(common: Common) {
    this.common = common;
  }

  async findAll(query: FindAllOptions = {}): Promise<ProviderWithEnabledPractices[]> {
    const url = this.common.constructPath(this.baseEndpoint, query);
    const { providers } = await this.common.getJSON(url);
    return providers;
  }

  async findAllPaginated(
    options: PaginatedQueryOptions<FindAllOptions>
  ): Promise<PaginationResponse<UserBasic>> {
    const url = this.common.constructPath(`${this.baseEndpoint}/basic`, options);
    return await this.common.getJSON(url);
  }

  async findAllBasic(query: FindAllOptions = {}): Promise<UserBasic[]> {
    const url = this.common.constructPath(`${this.baseEndpoint}/basic`, query);
    const { providers } = await this.common.getJSON(url);
    return providers;
  }

  async findAllBasicPaginated(
    query: PaginatedQueryOptions<FindAllOptions>
  ): Promise<PaginationResponse<UserBasic>> {
    const url = this.common.constructPath(`${this.baseEndpoint}/basic`, query);
    return await this.common.getJSON(url);
  }

  async findOneById(id: string): Promise<ProviderWithEnabledPractices> {
    const url = this.common.constructPath(`${this.baseEndpoint}/${id}`);
    const { provider } = await this.common.getJSON(url);
    return provider;
  }

  async findOneBasicById(id: string): Promise<UserBasic> {
    const url = this.common.constructPath(`${this.baseEndpoint}/${id}/basic`);
    const { provider } = await this.common.getJSON(url);
    return provider;
  }

  async upsert(values: UpsertValues): Promise<Provider> {
    const url = this.common.constructPath(`${this.baseEndpoint}`);
    return await this.common.putJSON(url, values);
  }

  async update(id: string, values: { npi: string }): Promise<Provider> {
    const url = this.common.constructPath(`${this.baseEndpoint}/${id}`);
    return await this.common.patchJSON(url, values);
  }

  async findOneByEhrProviderId(id: string): Promise<User> {
    const url = this.common.constructPath(`/ehr-provider-information/${id}/provider`);
    return await this.common.getJSON(url);
  }

  async findOneByEhrProviderValue(
    practiceId: string,
    value: string
  ): Promise<{
    provider: User;
    ehrProviderInformation: EhrProviderInformation;
  }> {
    const url = this.common.constructPath(`/ehr-provider-information/provider`, {
      practiceId,
      value,
    });
    return await this.common.getJSON(url);
  }

  async createEhrProviderInformation(ehrProviderInformation: {
    practiceId: string;
    providerUserId: string;
    value: string;
  }): Promise<EhrProviderInformation> {
    const url = this.common.constructPath('/ehr-provider-information/');
    return await this.common.postJSON(url, ehrProviderInformation);
  }

  async verifyImport(
    values: Record<string, string>[]
  ): Promise<Record<string, ImportValidation>[]> {
    const url = this.common.constructPath(`${this.baseEndpoint}/import/verify`);
    return await this.common.postJSON(url, { providers: values });
  }

  async import(values: CsvBulkUpdateValues[]) {
    const url = this.common.constructPath(`${this.baseEndpoint}`);
    return await this.common.patchJSON(url, { providers: values });
  }

  async csvVerify(values: Record<string, string>[]): Promise<Record<string, ImportValidation>[]> {
    const url = this.common.constructPath(`${this.baseEndpoint}/csv/verify`);
    return await this.common.postJSON(url, values);
  }

  async csvUpsert(values: ProviderCsv[]): Promise<{ success: boolean }> {
    const url = this.common.constructPath(`${this.baseEndpoint}/csv/`);
    return await this.common.putJSON(url, values);
  }
}
